import React, {useContext, useEffect, useState} from 'react';

import {useSelector} from 'react-redux';
import useWebSocket, {ReadyState} from 'react-use-websocket';
import {WebSocketHook} from 'react-use-websocket/dist/lib/types';

import {getEmail, getToken} from 'client/services/cookie-data-source';
import {useLanguage} from 'client/services/hooks';

import {interpolate} from 'client/ducks/language/helpers';
import {selectViewMode} from 'client/ducks/user/selectors';

import {API_URL} from 'client/common/config';
import {useToast} from 'client/common/hooks/useToast';

import {MediaStorage} from 'client/models/media-storage/types';

import {WS_CHANNELS} from './constants';
import {
  WsMessage,
  WsStatisticExportMessage,
  WsDataTabExportMessage,
  WsCouponTokenMessage,
  WsDuplicateOperationMessage,
} from './types';

type WsProviderProps = {
  children?: React.ReactNode | React.ReactNode[];
  connect: boolean;
};

const WsContext = React.createContext<WebSocketHook>({
  sendMessage: () => {},
  sendJsonMessage: () => {},
  lastMessage: null,
  lastJsonMessage: null,
  readyState: -1,
  getWebSocket: () => null,
});
const WS_URL = `${API_URL}/cable`.replace('http', 'ws');
const WS_RECONNECT_ATTEMPTS = 5;
const WS_RECONNECT_INTERVAL_MS = 3000;

const SYSTEM_MESSAGE_TYPES = ['confirm_subscription', 'welcome', 'ping'];

/**
 * WsProvider - WebSocket Provider for starting connection with WS server and defining global subscriptions (ex. for toast notifications)
 * If you need to make a special subscription on a page you can use `useWs` hook (without `connect` flag) there
 */
const WsProvider: React.FC<WsProviderProps> = ({children, connect}) => {
  const viewMode = useSelector(selectViewMode);
  let url = WS_URL + `?user_token=${getToken() || ''}&user_email=${getEmail() || ''}`;

  if (viewMode?.on && viewMode?.id) {
    url += `&fake_client_user_id=${viewMode.id}`;
  }

  const wsState = useWebSocket(
    url,
    {
      shouldReconnect: () => true,
      retryOnError: true,
      reconnectAttempts: WS_RECONNECT_ATTEMPTS,
      reconnectInterval: WS_RECONNECT_INTERVAL_MS,
    },
    connect,
  );
  const {sendJsonMessage, readyState, lastJsonMessage} = wsState;

  const [returnedWsState, setReturnedWsState] = useState(wsState);

  // filter system messages
  useEffect(() => {
    if (!SYSTEM_MESSAGE_TYPES.includes(wsState.lastJsonMessage?.type)) {
      setReturnedWsState(wsState);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsState.lastJsonMessage?.type]);

  const wsLang = useLanguage('WS_NOTIFICATIONS');
  const exportLangs = useLanguage('GENERATING_EXPORT_FILE');
  const duplicateOperationLang = useLanguage('OPERATIONS.OPERATION_DUPLICATION_MODAL.MESSAGES');

  const {appendToastNotification} = useToast();

  // subscribing
  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      Object.values(WS_CHANNELS).forEach((channel) => {
        sendJsonMessage({
          command: 'subscribe',
          identifier: channel,
        });
      });

      return () => {
        Object.values(WS_CHANNELS).forEach((channel) => {
          sendJsonMessage({
            command: 'unsubscribe',
            identifier: channel,
          });
        });
      };
    }
  }, [readyState, sendJsonMessage]);

  // handling events
  // some events need to be handled global in the whole app
  useEffect(() => {
    if (lastJsonMessage) {
      if ('message' in lastJsonMessage) {
        if ([WS_CHANNELS.STATISTIC_EXPORTS, WS_CHANNELS.DATA_TABS_EXPORTS].includes(lastJsonMessage.identifier)) {
          const {message} = lastJsonMessage as WsMessage<WsStatisticExportMessage | WsDataTabExportMessage>;

          appendToastNotification({
            type: message.success ? 'success' : 'error',
            title: interpolate(exportLangs.NOTIFICATION_TITLE?.toString(), {fileName: message.record.filename}),
            description: message.success ? exportLangs.NOTIFICATION_SUCCESS : exportLangs.NOTIFICATION_ERROR,
          });
        } else if (lastJsonMessage.identifier === WS_CHANNELS.COUPON_TOKENS) {
          const {message} = lastJsonMessage as WsMessage<WsCouponTokenMessage>;

          if (message.tokens_number || !message.success) {
            appendToastNotification({
              type: message.success ? 'success' : 'error',
              description: message.success
                ? interpolate(wsLang.COUPON_TOKEN_IMPORT_FINISHED?.toString(), {
                    number: message.tokens_number || 0,
                  })
                : wsLang.PRIZE_TOKEN_ERROR,
            });
          }
        } else if (lastJsonMessage?.identifier === WS_CHANNELS.MEDIA_UPLOAD) {
          const data: {status: string; media_storage: MediaStorage} = lastJsonMessage.message;
          if (data.status !== 'success') {
            appendToastNotification({
              type: 'error',
              title: wsLang.MEDIA_UPLOAD_ERROR?.toString(),
            });
          }
        } else if (lastJsonMessage?.identifier === WS_CHANNELS.WEB_APP_PUBLISH) {
          const data: {status: string; action: string; error: string; error_code?: string} = lastJsonMessage.message;
          if (data.action !== 'web_app_testing' || data.status === 'error') {
            const errorMessage =
              wsLang.WEB_APP_PUBLISH_ERRORS[
                String(data.error_code).toUpperCase() as keyof typeof wsLang.WEB_APP_PUBLISH_ERRORS
              ] || wsLang.PROCESS_ERROR;
            appendToastNotification({
              type: data.status === 'success' ? 'success' : 'error',
              title: data.status === 'success' ? wsLang.PROCESS_FINISHED : errorMessage,
            });
          }
        } else if (lastJsonMessage.identifier === WS_CHANNELS.DUPLICATE_OPERATION) {
          const {message} = lastJsonMessage as WsMessage<WsDuplicateOperationMessage>;
          const {success} = message;
          appendToastNotification({
            type: success ? 'success' : 'error',
            title: success
              ? interpolate(duplicateOperationLang.DUPLICATION_FINISHED, {name: message.source_operation_name})
              : interpolate(duplicateOperationLang.DUPLICATION_FAILED, {name: message.source_operation_name}),
            description: success ? message.operation?.name : '',
          });
        }
      }
    }
  }, [appendToastNotification, lastJsonMessage, exportLangs, wsLang, duplicateOperationLang]);

  return <WsContext.Provider value={returnedWsState}>{children}</WsContext.Provider>;
};

export const useWs = () => useContext(WsContext);

export default WsProvider;
